import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Grid,
  Collapse,
  Button,
  Skeleton
} from "@mui/material";
import { api } from "../services/api";
import { toast } from "react-toastify";
import azurelogo from "../assets/azurelogo.svg";
import awssvgrep from "../assets/Amazon_Web_Services_Logo.svg";
import gcpIcon from "../assets/gcpIcon.svg";
import vmware from "../assets/Vmware.svg";
import { useSelectedProvider } from "../services/selectedProiderContext";
import { useNavigate } from "react-router-dom";

const AWS = "aws";
const AZURE = "azure";
const ACTIVE_ACCOUNT_ID = "activeAccountId";
const DARK_MODE = "dark";

const providerLogoMap = {
  [AWS]: awssvgrep,
  [AZURE]: azurelogo,
  gcp: gcpIcon,
  vmware: vmware,
};

const getNormalizedProvider = (p) => (p.cloudProvider ?? p.CloudProvider ?? p.cloudprovider ?? "").toString().trim().toLowerCase();
const getProjId = (p) => p.projectId ?? p.ProjectId ?? p.projectid;
const getProjName = (p) => p.projectName ?? p.ProjectName ?? p.projectname ?? "Unnamed Project";

const getDisplayId = (providerKey, p) => {
  if (providerKey === AWS) return p.accountId ?? p.AccountId ?? p.accountid;
  if (providerKey === AZURE) return p.subscriptionId ?? p.SubscriptionId ?? p.subscriptionid;
  return getProjId(p);
};

const getProviderBgColor = (themeObj, isSelected) => {
  if (themeObj.palette.mode === DARK_MODE) {
    return isSelected ? "rgba(144, 202, 249, 0.24)" : "background.default";
  }
  return isSelected ? "#eef1fc" : "#eaeeff";
};

const getProjectBgColor = (themeObj) => {
  return themeObj.palette.mode === DARK_MODE ? "rgba(255, 255, 255, 0.05)" : "#eef1fc";
};

const getProjectBorder = (themeObj, isSelectedProject) => {
  const isDarkMode = themeObj.palette.mode === DARK_MODE;
  if (isSelectedProject) {
    return `2px solid ${isDarkMode ? '#90caf9' : '#1976d2'}`;
  }
  return `1px solid ${isDarkMode ? themeObj.palette.divider : '#e0e0e0'}`;
};

const clearStaleProviderKeys = (nextProviderKey) => {
  // Always clear Azure-only keys unless Azure is the active provider choice.
  if (nextProviderKey !== AZURE) {
    localStorage.removeItem("selectedSubscription");
    sessionStorage.removeItem("selectedSubscription");
  }

  // Clear non-target provider keys to avoid stale ids leaking into forms.
  if (nextProviderKey !== AWS && nextProviderKey !== AZURE) {
    localStorage.removeItem("selectedAccountId");
  }
  if (nextProviderKey !== "gcp") {
    localStorage.removeItem("selectedGcpProjectId");
  }
};

const AccountSelector = ({ onSuccess, mode = "initial" }) => {
  const navigate = useNavigate();
  const { selectAccount } = useSelectedProvider();

  const [projects, setProjects] = useState([]);
  const [cloudProviders, setCloudProviders] = useState([]);
  const [selectedProviderKey, setSelectedProviderKey] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [search, setSearch] = useState("");
  
  const [providersLoading, setProvidersLoading] = useState(true);
  const [loading, setLoading] = useState(true); 

  const [dynamicUserId, setDynamicUserId] = useState(
    sessionStorage.getItem("userId") 
  );
  
  const providerSelected = Boolean(selectedProviderKey);
  const activeAccountId = sessionStorage.getItem(ACTIVE_ACCOUNT_ID);

  const hasAutoSelected = useRef(false);
  const prevProviderKey = useRef(selectedProviderKey);

  /* ---------------- API CALLS ---------------- */
  useEffect(() => {
    setProvidersLoading(true);
    api.get("/Client/cloudProviders")
      .then((res) => setCloudProviders(res.data))
      .catch((err) => console.error("Failed to load providers", err))
      .finally(() => setProvidersLoading(false));
  }, []);

  useEffect(() => {
    const isValidUser = dynamicUserId && dynamicUserId !== "null" && dynamicUserId !== "undefined";
    if (isValidUser) return;

    const intervalId = setInterval(() => {
      const id = sessionStorage.getItem("userId");
      if (id && id !== "null" && id !== "undefined") {
        setDynamicUserId(id); 
        clearInterval(intervalId);
      }
    }, 200);

    return () => clearInterval(intervalId);
  }, [dynamicUserId]);

  useEffect(() => {
    const fetchProjects = async (validUserId) => {
      setLoading(true);
      try {
        const res = await api.get(`/Project/getProjectDetails/${validUserId}`);
        setProjects(res.data);
      } catch (err) {
        console.error(err);
        
        if (err.response && err.response.status === 403) {
          toast.error("Access Denied: Backend has not updated this route for L0-L4 roles.");
        } else {
          toast.error("Failed to load accounts");
        }
      } finally {
        setLoading(false);
      }
    };

    const isValidUser = dynamicUserId && dynamicUserId !== "null" && dynamicUserId !== "undefined";
    if (isValidUser) {
      fetchProjects(dynamicUserId);
    } else {
      const timer = setTimeout(() => setLoading(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [dynamicUserId]);

  useEffect(() => {
    if (mode === "switch") {
      const storedProvider = localStorage.getItem("selectedProvider");
      if (storedProvider) {
        setSelectedProviderKey(storedProvider.toLowerCase());
      }
    }
  }, [mode]);

  useEffect(() => {
    if (!projects.length) return;

    const providerChanged = prevProviderKey.current !== selectedProviderKey;

    if (!hasAutoSelected.current || providerChanged) {
      if (selectedProviderKey) {
        const activeProject = projects.find((p) => {
          const pKey = getNormalizedProvider(p);
          return pKey === selectedProviderKey &&
            (p.accountId === activeAccountId ||
             p.subscriptionId === activeAccountId ||
             p.projectId === activeAccountId);
        });
        
        if (activeProject) {
          setSelectedProjectId(getProjId(activeProject));
        }
      } else if (mode === "switch" && activeAccountId) {
        const activeProject = projects.find((p) =>
          p.accountId === activeAccountId ||
          p.subscriptionId === activeAccountId ||
          p.projectId === activeAccountId
        );
        if (activeProject) {
          setSelectedProjectId(getProjId(activeProject));
        }
      }

      hasAutoSelected.current = true;
      prevProviderKey.current = selectedProviderKey;
    }
  }, [projects, selectedProviderKey, activeAccountId, mode]);


  /* ---------------- DATA PROCESSING ---------------- */
  const filteredProjects = projects.filter((p) => {
    const providerName = getNormalizedProvider(p);
    if (providerName !== (selectedProviderKey || "").toLowerCase()) {
      return false;
    }

    const query = search.trim().toLowerCase();
    if (!query) return true;

    const rawId = getDisplayId(selectedProviderKey, p);
    const rawName = getProjName(p);

    const stringId = rawId !== undefined && rawId !== null ? String(rawId).toLowerCase() : "";
    const stringName = String(rawName).toLowerCase();

    return stringId.includes(query) || stringName.includes(query);
  });

  /* ---------------- ACTION ---------------- */
  const handleProceed = async () => {
    const selectedProject = projects.find((p) => getProjId(p) === selectedProjectId);
    if (!selectedProject) return;

    const providerLow = selectedProject.cloudProvider.toLowerCase();
    const providerEntry = cloudProviders.find((p) => p.text.toLowerCase() === providerLow);

    if (!providerEntry) {
      toast.error("Selected cloud provider not found");
      return;
    }

    const activeAccId = getDisplayId(providerLow, selectedProject);

    try {
      const res = await api.post(`/Account/${dynamicUserId}/${activeAccId}`);
      
      if (res.status === 200 && res.data && res.data.success) {
        
        selectAccount(selectedProject, providerEntry.value, activeAccId, res.data);

        const rawAccountName = getProjName(selectedProject);
        const maxLength = 30;
        const accountDisplayName = rawAccountName.length > maxLength 
          ? `${rawAccountName.substring(0, maxLength)}...` 
          : rawAccountName;

        toast.success(
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span style={{ fontWeight: 500 }}>
              {mode === "switch"
                ? `Switched to ${accountDisplayName}`
                : `Account ${accountDisplayName} selected successfully`}
            </span>
            <span style={{ fontSize: "12px", opacity: 0.8, marginTop: "4px" }}>
              Role: {res.data.activeRole} | ID: {activeAccId}
            </span>
          </div>
        );
        
        if (onSuccess) {
          onSuccess(providerLow, res.data.activeAccountId || activeAccId);
        }
        
        if (mode !== "switch") {
          navigate("/Resources", { state: { provider: providerLow } });
        }
      } else if (res.data && res.data.success === false) {
        toast.error("Failed to switch account on the server.");
      }
    } catch (err) {
      console.error("Account switch failed:", err);
      
      if (err.response && err.response.status === 403) {
        toast.error("You do not have permission to access this account.");
      } else {
        toast.error("Error updating active account");
      }
    }
  };

  const currentSelectedProject = projects.find((p) => getProjId(p) === selectedProjectId);
  let selectedAccountId = null;
  if (currentSelectedProject) {
    selectedAccountId = getDisplayId(selectedProviderKey, currentSelectedProject);
  }

  const isSameAccount = selectedAccountId === activeAccountId;
  const isSelectedVisible = filteredProjects.some((p) => getProjId(p) === selectedProjectId);

  /* ---------------- RENDER HELPER ---------------- */
  const renderProjectList = () => {
    if (loading) {
      return (
        <Grid container spacing={2} justifyContent="center">
          {[...Array(6)].map((_, index) => (
            <Grid size="auto" sx={{ display: "flex", justifyContent: "center" }} key={index}>
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 3,
                  boxShadow: "none",
                  textAlign: "center",
                  minWidth: "200px",
                  backgroundColor: (t) => getProjectBgColor(t),
                  border: (t) => getProjectBorder(t, false),
                }}
              >
                <Skeleton variant="text" width="70%" height={24} sx={{ mx: "auto" }} />
                <Skeleton variant="text" width="40%" height={16} sx={{ mx: "auto", mt: 0.5 }} />
              </Paper>
            </Grid>
          ))}
        </Grid>
      );
    }

    if (!selectedProviderKey) {
      return (
        <Box>
          <Typography sx={{ textAlign: "center", mt: 4, color: "text.secondary", fontWeight: 500 }}>
            Please select a cloud provider above to view accounts.
          </Typography>
        </Box>
      );
    }

    if (filteredProjects.length === 0 && !loading) {
      return (
        <Box>
          <Typography sx={{ textAlign: "center", mt: 4, color: "text.secondary", fontWeight: 500 }}>
            No accounts or subscriptions found for {selectedProviderKey.toUpperCase()}.
          </Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={2} justifyContent="center">
        {filteredProjects.map((project, index) => {
          const displayId = getDisplayId(selectedProviderKey, project);
          const displayName = getProjName(project);
          
          // Fallback to absolute index removed so search filter doesn't misalign selection keys
          const projectIdKey = getProjId(project) || displayId; 
          const isSelectedProject = selectedProjectId === projectIdKey;

          return (
            <Grid 
              size="auto" 
              sx={{ display: "flex", justifyContent: "center" }} 
              key={projectIdKey || index}
            >
              <Paper
                onClick={() => setSelectedProjectId(projectIdKey)}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  cursor: "pointer",
                  boxShadow: "none",
                  textAlign: "center",
                  minWidth: "200px",
                  backgroundColor: (t) => getProjectBgColor(t),
                  border: (t) => getProjectBorder(t, isSelectedProject),
                }}
              >
                <Typography fontWeight="bold" sx={{ color: "text.primary" }}>
                  {displayName}                       
                </Typography>
                <Typography sx={{fontSize: "12px", pt:0.5, color: "text.secondary"}}>
                  {displayId || "Unknown ID"}
                </Typography>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  /* ---------------- UI ---------------- */
  return (
    <Box
      sx={{
        p: 3,
        display: "flex",
        flexDirection: "column",
        width: '100%',
        transition: "all 0.5s ease",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          transition: "all 0.5s ease",
          mt: providerSelected ? 1 : 8,
        }}
      >
        {!providerSelected && (
         <Typography
          variant={providerSelected ? "h6" : "h4"}
          sx={{
            fontWeight: 600,
            mb: providerSelected ? 2 : 4,
            transition: "all 0.4s ease",
            color: "text.primary",
          }}
        >
          Choose Provider
        </Typography>
        )}
        
        <Box
          sx={{
            display: "flex",
            gap: providerSelected ? 1.5 : 3,
            transition: "all 0.4s ease"
          }}
        >
          {providersLoading ? (
            [...Array(4)].map((_, idx) => (
              <Paper
                key={idx}
                sx={{
                  width: providerSelected ? 56 : 220,
                  height: providerSelected ? 56 : 100,
                  p: providerSelected ? 1 : 3,
                  borderRadius: 2,
                  display: "flex",
                  flexDirection: providerSelected ? "row" : "column",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: (t) => getProviderBgColor(t, false),
                  boxShadow: "none",
                }}
              >
                <Skeleton 
                  variant="circular" 
                  width={providerSelected ? 28 : 45} 
                  height={providerSelected ? 28 : 45} 
                  sx={{ mb: providerSelected ? 0 : 1 }}
                />
                {!providerSelected && (
                  <Skeleton variant="text" width={100} height={20} />
                )}
              </Paper>
            ))
          ) : (
            cloudProviders.map((provider) => {
              const key = provider.text.toLowerCase();
              const selected = selectedProviderKey === key;

              return (
                <Paper
                  key={provider.value}
                  onClick={() => {
                    clearStaleProviderKeys(key);
                    setSelectedProviderKey(key);
                    setSelectedProjectId(null); // Explicitly clears so you don't keep old selections
                    setSearch("");
                  }}
                  sx={{
                    width: providerSelected ? 56 : 220,
                    height: providerSelected ? 56 : "auto",
                    p: providerSelected ? 1 : 3,
                    borderRadius: 2,
                    display: "flex",
                    flexDirection: providerSelected ? "row" : "column",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    backgroundColor: (t) => getProviderBgColor(t, selected),
                    boxShadow: selected ? 4 : "none",
                    transition: "all 0.45s ease",
                    "&:hover": {
                      backgroundColor: (t) => t.palette.mode === DARK_MODE ? "rgba(0, 0, 0, 0.5)" : "#3f51b54f",
                    },
                  }} 
                >
                  <img
                    src={providerLogoMap[key]}
                    alt={provider.text}
                    style={{
                      width: providerSelected ? 28 : 52,
                      marginBottom: providerSelected ? 0 : 12,
                      transition: "all 0.4s ease",
                      minHeight: providerSelected ? 28 : 52,
                    }}
                  />

                  {!providerSelected && (
                    <Typography fontWeight="bold" sx={{ color: "text.primary" }}>
                      {provider.text}
                    </Typography>
                  )}
                </Paper>
              );
            })
          )}
        </Box>
      </Box>

      <Collapse in={providerSelected} timeout={500}>
        <Box
          sx={{
            width: "60%",
            mx: "auto",
            px: 3,
            my: 4,
            display: "flex", justifyContent: "center"
          }}
        >
          <TextField
            fullWidth
            placeholder="Search Account / Subscription ID or Name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            inputProps={{ style: { textAlign: "center" } }}
          />
        </Box>

        <Box
          sx={{
            width: "100%",
            mx: "auto",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box 
            sx={{
              flexGrow: 1, 
              overflowY: "auto", 
              maxHeight: "280px",
              px: 1, 
              "&::-webkit-scrollbar": { width: "8px" },
              "&::-webkit-scrollbar-track": { 
                background: (t) => t.palette.mode === DARK_MODE ? "rgba(255, 255, 255, 0.05)" : "#e0e4f794", 
                borderRadius: "8px" 
              },
              "&::-webkit-scrollbar-thumb": { 
                backgroundColor: (t) => t.palette.mode === DARK_MODE ? "rgba(255, 255, 255, 0.2)" : "#6c7ae0", 
                borderRadius: "8px" 
              },
            }}
          >
            {renderProjectList()}
          </Box>

          {selectedProjectId && !isSameAccount && isSelectedVisible && (
            <Box sx={{ textAlign: "center", mt: 5 }}>
              <Button variant="contained" sx={{fontSize: "12px"}} onClick={handleProceed}>
                Proceed
              </Button>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default AccountSelector;